import { CheckBox } from "components/FilterCheckList/elements/CheckBox";
import { Number } from "components/Number";
import { device, mq } from "lib/media-query";
import React, { useState } from "react";
import IconTextButton from "components/IconTextButton";
import styled from "styled-components";
import ArrowDown from "svg/arrow-down.svg?sprite";
import { DL_FILTER_EVENT, DL_FILTER_ACTION_ON, DL_FILTER_ACTION_OFF } from "helpers/constants";
import { slugify } from "helpers/slugify";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";

const Container = styled.ul`
    width: fit-content;
    columns: ${({ columns }) => columns};
    column-gap: 36px;
    margin: 0;
    padding: 4px 0 8px 8px;
    list-style-type: none;

    ${mq.to(device.TABLET)`
    columns: 1;
  `};
`;

const Option = styled.li`
    height: 100%;
    padding: 8px 0;
`;

const Label = styled.label`
    margin: 0;
    line-height: 16px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
`;

const DisplayName = styled.div`
    text-wrap: avoid;
`;

const Count = styled.div`
    display: inline;
    margin: 0 0 0 4px;
    color: ${({ theme }) => theme.colors.tertiary[700]};
`;

const Placeholder = styled.div`
    height: 16px;
`;

function pushToDataLayer(key, value, action) {
    datalayerWrapper({
        event: DL_FILTER_EVENT,
        filter_action: action,
        filter_name: key,
        filter_value: value.toLowerCase()
    });
}

const FilterCheckList = ({
    filterId,
    value: defaultValue,
    datalayerKey: defaultDatalayerKey,
    handleChange: defaultOnChange,
    options,
    isSingle,
    maxSize = 5,
    columns = 1,
    dataTestId = "filter-check-list"
}) => {
    const [showAll, setShowAll] = useState(maxSize === 0);

    if (!options.length) {
        return null;
    }

    // Use an onChange factory so that the onChange, datalayerKey, and value can be overridden for each option
    // while falling back to the default ones provided to the FilterCheckList component. This is needed for the
    // GarantiesEnZekerhedenFilter at the moment to combine multiple filters into a single FilterCheckList.
    const makeOnChangeHandler = (onChange, datalayerKey, value) => {
        return (event) => {
            const { value: changedId, name } = event.target;
            if (value.includes(changedId)) {
                pushToDataLayer(datalayerKey, name, DL_FILTER_ACTION_OFF);
                const val = typeof value === "string" ? [value] : value;
                onChange(
                    filterId,
                    val.filter((id) => id !== changedId)
                );
                return;
            }
            pushToDataLayer(datalayerKey, name, DL_FILTER_ACTION_ON);
            const val = isSingle ? [changedId] : [...value, changedId];
            onChange(filterId, val);
        };
    };

    const displayOptions = options.filter(
        (option, i) =>
            showAll ||
            defaultValue?.includes(option.id) ||
            option.value?.includes(option.id) ||
            i < maxSize
    );

    return (
        <div data-testid={dataTestId}>
            <Container columns={columns}>
                {displayOptions.map(
                    ({
                        id,
                        displayName,
                        aantal,
                        naam,
                        onChange = defaultOnChange,
                        datalayerKey = defaultDatalayerKey,
                        value = defaultValue
                    }) => (
                        <Option key={displayName}>
                            <Label data-testid={`${slugify(displayName)}-option`}>
                                <CheckBox
                                    name={naam || displayName}
                                    value={id}
                                    checked={value.includes(id)}
                                    onChange={makeOnChangeHandler(onChange, datalayerKey, value)}
                                />
                                <DisplayName>
                                    {displayName}
                                    {aantal !== undefined && (
                                        <Count>
                                            (<Number value={aantal} />)
                                        </Count>
                                    )}
                                </DisplayName>
                            </Label>
                        </Option>
                    )
                )}
                {/* add placeholders to fill the missing spaces so the columns are rendered correctly */}
                {options.length % columns > 0 &&
                    new Array(columns - (options.length % columns || columns))
                        .fill(0)
                        .map((_, i) => `placeholder-${i}`)
                        .map((key) => (
                            <Option key={key}>
                                <Placeholder />
                            </Option>
                        ))}
            </Container>

            {options.length > maxSize && !showAll && (
                <IconTextButton onClick={() => setShowAll(true)}>
                    Laat alle {options.length} opties zien <ArrowDown />
                </IconTextButton>
            )}
        </div>
    );
};

export default FilterCheckList;
